import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { QUESTION_TYPES } from '@api/instant_survey';

import { Announcement, Button, GPTIcon, OverviewIcon } from '@utilities';

import { getQuestionCost } from '../../../../utilities/helpers';
import ModalLogicChangeConfirmation from './components/ModalLogicChangeConfirmation';
import Question from './components/Question';
import PreviewBanner from './components/PreviewBanner';
import QuestionPlaceholder from './components/QuestionPlaceholder';
import { SurveyContext } from '../../../../utilities/context';

import rocketError from './assets/rocket-error.svg';

import styles from './_index.module.scss';

const QuestionnaireList = ({
  calculateQuestionNumbers,
  firstErrorRef,
  generationError,
  generationErrorMessage,
  isDetailedQuestionView,
  isVideoSurvey,
  onAcceptPreview,
  onCancelPreview,
  onCreateQuestion,
  onDuplicateQuestion,
  onEditQuestion,
  onOpenAIGenerator,
  onRemoveQuestion,
  onReprompt,
  pipingQuestionsMap,
  questions,
  showActions,
  showPreviewBanner,
}) => {
  const { setShowSurveyTemplateModal, survey, updateSurvey } = useContext(SurveyContext);
  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canUseAIGenerationQuestionnaire } = permissions;
  const [questionBeingMoved, setQuestionBeingMoved] = useState(null);
  const [questionsRequiringLogicConfirmation, setQuestionsRequiringLogicConfirmation] =
    useState(null);

  const onDragEnd = (result) => {
    if (!result?.destination) {
      return;
    }
    const destinationIndex = result?.destination?.index;
    const sourceIndex = result?.source?.index;
    if (destinationIndex === sourceIndex) {
      return;
    }
    const updatedQuestions = [...survey.questions];
    const [questionToMove] = updatedQuestions.splice(sourceIndex, 1);
    updatedQuestions.splice(destinationIndex, 0, questionToMove);

    // Recalculate question numbers based on new order
    const updatedQuestionNumbers = calculateQuestionNumbers(updatedQuestions);

    const hasLogicChanges = updatedQuestions.some(({ id }, index) => {
      const currentLogic = survey?.questionLogic[id];

      if (
        // only first two questions (ignoring Instructional Text) can disqualify
        currentLogic?.disqualify &&
        updatedQuestions
          .slice(0, index)
          .filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value).length >= 2
      ) {
        return true;
      }

      if (
        currentLogic?.skip?.logic.some(
          // skip logic cannot reference questions that are after the current question
          ({ question }) =>
            question &&
            updatedQuestions.findIndex(({ id: questionId }) => question.id === questionId) > index
        )
      ) {
        return true;
      }
      return false;
    });

    const hasPipingQuestionChanges = updatedQuestions.some(({ id }, index) => {
      const children = pipingQuestionsMap.childrenByParent[id] || [];
      const hasChildrenAllAfterParent = children.every(
        ({ questionId }) => updatedQuestionNumbers[questionId] > updatedQuestionNumbers[id]
      );
      const parents = pipingQuestionsMap.parentsByChild[id] || [];
      const hasParentsAllBeforeChild = parents.every(
        ({ questionId }) => updatedQuestionNumbers[questionId] < updatedQuestionNumbers[id]
      );

      if (!hasParentsAllBeforeChild || !hasChildrenAllAfterParent) {
        return true;
      } else if (hasChildrenAllAfterParent) {
        children.forEach(({ questionId }) => {
          //  update question text with new parent question index
          updatedQuestions.find((q) => q.id === questionId).text = updatedQuestions
            .find((q) => q.id === questionId)
            .text.replace(
              /\{answer from q(\d+)/g,
              `{answer from q${updatedQuestionNumbers[pipingQuestionsMap.parentsByChild[questionId][0].questionId]}`
            );
        });
      }
      return false;
    });

    if (hasLogicChanges || hasPipingQuestionChanges) {
      setQuestionBeingMoved(questionToMove);
      setQuestionsRequiringLogicConfirmation(updatedQuestions);
    } else {
      updateSurvey({ questions: updatedQuestions });
    }
  };

  return (
    <>
      <div className={styles['questionnaire-list-container']}>
        {questions.length === 0 &&
          !isVideoSurvey &&
          canUseAIGenerationQuestionnaire &&
          !generationError && (
            <Announcement
              className={styles['questionnaire-list-announcement']}
              elevation={1}
              header="Introducing AI Generated Questionnaires"
              icon={<GPTIcon />}
              text={
                <>
                  Writer's block? Get started with AI suggested questions. Try an{' '}
                  <span
                    className={styles['questionnaire-list-banner-link']}
                    onClick={onOpenAIGenerator}
                  >
                    AI Generated Questionnaire
                  </span>{' '}
                  now.
                </>
              }
              variant="ai"
            />
          )}
        {questions.length === 0 && !generationError && (
          <Announcement
            className={styles['questionnaire-list-announcement']}
            elevation={1}
            header="Questionnaire Templates"
            icon={<OverviewIcon />}
            text={
              <>
                Use one of our{' '}
                <span
                  className={styles['questionnaire-list-banner-link']}
                  onClick={() => setShowSurveyTemplateModal(true)}
                >
                  Questionnaire Templates
                </span>{' '}
                to quickly get started.
              </>
            }
            variant="info"
          />
        )}
        {(showPreviewBanner || generationError) && (
          <PreviewBanner
            generationError={generationError}
            generationErrorMessage={generationErrorMessage}
            onAcceptPreview={onAcceptPreview}
            onCancelPreview={onCancelPreview}
            onReprompt={onReprompt}
          />
        )}
        {generationError && !showPreviewBanner && (
          <div className={styles['questionnaire-list-ai-error']}>
            <img alt="Error" src={rocketError} />
            <p className={styles['questionnaire-list-ai-error-message']}>
              Houston, we have a problem
            </p>
            {generationErrorMessage && (
              <p className={styles['questionnaire-list-ai-error-detail']}>
                {generationErrorMessage}
              </p>
            )}
            {onReprompt && <Button onClick={onReprompt} text="Try Again" variant="ai" />}
          </div>
        )}
        {questions.length === 0 && !generationError && (
          <QuestionPlaceholder onCreateQuestion={onCreateQuestion} />
        )}
        {questions.length > 0 && !generationError && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div
                  className={styles['selections']}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {(() => {
                    let questionIndex = 0; // Needed to skip instructional text indices
                    return questions.map((question, index) => {
                      const questionCost = getQuestionCost(question);
                      const startingIndex = questionIndex + 1;
                      const endingIndex = questionIndex + questionCost;

                      const displayNumber =
                        question.type === QUESTION_TYPES.INSTRUCTIONS.value
                          ? 'T'
                          : `${startingIndex}${endingIndex > startingIndex ? '-' + endingIndex : ''}`;
                      questionIndex = questionIndex + questionCost;

                      return (
                        <Draggable
                          draggableId={`activeAnswer-${index}`}
                          index={index}
                          key={`activeAnswer-${index}`}
                        >
                          {(provided) => {
                            return (
                              <Question
                                firstErrorRef={firstErrorRef}
                                index={index}
                                isDetailedQuestionView={isDetailedQuestionView}
                                key={`question-${question.id}`}
                                number={displayNumber}
                                onDuplicateQuestion={onDuplicateQuestion}
                                onEditQuestion={onEditQuestion}
                                onRemoveQuestion={onRemoveQuestion}
                                provided={provided}
                                question={question}
                                questionLogic={survey?.questionLogic}
                                selectedQuestions={questions}
                                setQuestionLogic={(questionLogic) =>
                                  updateSurvey({ questionLogic })
                                }
                                showActions={showActions}
                              />
                            );
                          }}
                        </Draggable>
                      );
                    });
                  })()}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
      {questionsRequiringLogicConfirmation && (
        <ModalLogicChangeConfirmation
          calculateQuestionNumbers={calculateQuestionNumbers}
          onClose={() => setQuestionsRequiringLogicConfirmation(null)}
          pipingQuestionsMap={pipingQuestionsMap}
          questionBeingMoved={questionBeingMoved}
          questionLogic={survey?.questionLogic}
          questions={questionsRequiringLogicConfirmation}
          updateSurvey={updateSurvey}
        />
      )}
    </>
  );
};

QuestionnaireList.propTypes = {
  calculateQuestionNumbers: PropTypes.func.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  generationError: PropTypes.bool,
  generationErrorMessage: PropTypes.string,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  isVideoSurvey: PropTypes.bool.isRequired,
  onAcceptPreview: PropTypes.func,
  onCancelPreview: PropTypes.func,
  onCreateQuestion: PropTypes.func.isRequired,
  onDuplicateQuestion: PropTypes.func.isRequired,
  onEditQuestion: PropTypes.func.isRequired,
  onOpenAIGenerator: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  onReprompt: PropTypes.func,
  pipingQuestionsMap: PropTypes.object.isRequired,
  questions: PropTypes.array,
  showActions: PropTypes.bool,
  showPreviewBanner: PropTypes.bool,
};

export default QuestionnaireList;
